import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
/**
 * Minimal accessible menu dropdown — the repo's first menu primitive. A trigger
 * button (styled as the secondary `Button`) opens a list of items. Supports
 * keyboard navigation (↑/↓/Home/End, Enter/Space/↓ to open), focus management
 * (focus moves into the menu on open and returns to the trigger on close), and
 * closes on outside-click, Escape, or Tab. Disabled/loading states live on the
 * trigger, mirroring `Button`.
 */
export function Dropdown({ label, items, disabled = false, loading = false, className = '', }) {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef(null);
    const itemRefs = useRef([]);
    const menuId = useId();
    function focusTrigger() {
        containerRef.current?.querySelector('[aria-haspopup="menu"]')?.focus();
    }
    function close({ returnFocus = true } = {}) {
        setOpen(false);
        if (returnFocus)
            focusTrigger();
    }
    function openAt(index) {
        if (disabled || loading)
            return;
        setActiveIndex(index);
        setOpen(true);
    }
    // Move focus to the active item whenever the menu opens or the index changes.
    useEffect(() => {
        if (open)
            itemRefs.current[activeIndex]?.focus();
    }, [open, activeIndex]);
    // Close on a click outside the dropdown while it is open.
    useEffect(() => {
        if (!open)
            return;
        function onPointerDown(event) {
            if (!containerRef.current?.contains(event.target))
                setOpen(false);
        }
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);
    function onTriggerKeyDown(event) {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openAt(0);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            openAt(items.length - 1);
        }
    }
    function onMenuKeyDown(event) {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex((i) => (i + 1) % items.length);
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex((i) => (i - 1 + items.length) % items.length);
                break;
            case 'Home':
                event.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                event.preventDefault();
                setActiveIndex(items.length - 1);
                break;
            case 'Escape':
                event.preventDefault();
                close();
                break;
            case 'Tab':
                // Let focus leave naturally, but collapse the menu behind it.
                setOpen(false);
                break;
        }
    }
    function selectItem(item) {
        if (item.disabled)
            return;
        item.onSelect();
        close();
    }
    return (_jsxs("div", { ref: containerRef, className: `relative inline-block ${className}`, children: [_jsxs(Button, { variant: "secondary", loading: loading, disabled: disabled, "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": open ? menuId : undefined, onClick: () => (open ? close({ returnFocus: false }) : openAt(0)), onKeyDown: onTriggerKeyDown, children: [label, _jsx("span", { "aria-hidden": "true", className: "text-[12px] leading-none", children: "\u25BE" })] }), open && (_jsx("div", { id: menuId, role: "menu", "aria-label": typeof label === 'string' ? label : undefined, onKeyDown: onMenuKeyDown, className: "absolute left-0 top-full z-50 mt-1 min-w-[10rem] rounded-[12px] border border-outline-variant bg-surface-container-low p-1 shadow-[0_4px_16px_rgba(25,28,29,0.12)]", children: items.map((item, index) => (_jsx("button", { ref: (el) => {
                        itemRefs.current[index] = el;
                    }, type: "button", role: "menuitem", tabIndex: index === activeIndex ? 0 : -1, disabled: item.disabled, onClick: () => selectItem(item), className: [
                        'flex h-10 w-full items-center rounded-[8px] px-3 text-left text-[14px] font-medium',
                        'text-on-surface hover:bg-surface-container',
                        'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary',
                        item.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                    ].join(' '), children: item.label }, item.label))) }))] }));
}
